import { ProductData } from '@appcharge/shared-ui';
import {
  OfferData,
  ResponseTabData,
  TimeFrameObject,
} from '../../constants/apiResponses.types';
import { EOfferType, EPopupSubType } from '../../constants/enums';

export type EOfferState = 'collectable' | 'locked' | null;

export type ETriggerName =
  | 'PostPurchase'
  | 'StoreRefresh'
  | 'UserClick'
  | 'TimerEnded';

interface PartitionedOffers {
  offers: OfferData[];
  progressBars: OfferData[];
}

export const partitionOffersByType = (events: OfferData[]): PartitionedOffers => {
  const offers: OfferData[] = [];
  const progressBars: OfferData[] = [];
  for (const event of events) {
    if (!event?.offerType) continue;
    if (event.offerType === EOfferType.PROGRESS_BAR) {
      progressBars.push(event);
    } else {
      offers.push(event);
    }
  }
  return { offers, progressBars };
};

export const computeTriggerName = (offer: OfferData): ETriggerName => {
  if (offer.subType === EPopupSubType.POST_PURCHASE) return 'PostPurchase';
  if (offer.subType === EPopupSubType.STORE_REFRESH) return 'StoreRefresh';
  if (
    offer.subType === EPopupSubType.DAILY_BONUS ||
    offer.subType === EPopupSubType.REWARD_CALENDAR
  )
    return 'TimerEnded';
  return 'UserClick';
};

export const computeOfferState = (offer: OfferData): EOfferState => {
  if (offer.isAllowedToCollect == null) return null;
  return offer.isAllowedToCollect ? 'collectable' : 'locked';
};

export const computeBadgeInd = (offer: OfferData): boolean => {
  const subType = offer.subType;
  const hasBadgeImage = Boolean(offer.offerUi?.badgeCoverImage);
  const isBadgeSubType =
    subType === EPopupSubType.DAILY_BONUS ||
    subType === EPopupSubType.STORE_REFRESH;
  return hasBadgeImage && isBadgeSubType;
};

export const computeTimeLeftSec = (offer: OfferData): number | null => {
  const endMs =
    offer.subType === EPopupSubType.REWARD_CALENDAR && offer.rewardCalendar?.nextUnlockAt != null
      ? new Date(offer.rewardCalendar.nextUnlockAt).getTime()
      : getActiveTimeFrameEndsAtMs(offer.schedule);
  if (endMs == null) return null;
  const remainingMs = endMs - Date.now();
  if (!Number.isFinite(remainingMs)) return null;
  return Math.max(0, Math.floor(remainingMs / 1000));
};

export const arrangeMixpanelData = (data: OfferData[], tabs?: ResponseTabData[]) => {
  return data.map((offer: OfferData) => {
    const activeIndex = offer.indexToCollect ? offer.indexToCollect - 1 : 0;
    const isRewardCalendar = offer.subType === EPopupSubType.REWARD_CALENDAR;
    return {
      offer_id: offer.offerId,
      external_id: offer.publisherOfferId,
      name: offer.offerName,
      tab: tabs?.find((tab: ResponseTabData) => tab.tabId === offer.offerTabId)?.name,
      sale_percentage: offer.productSale?.sale,
      type: offer.offerType,
      priority: offer.priority ?? null,
      index: isRewardCalendar
        ? (offer.rewardCalendar != null ? offer.rewardCalendar.unlockedIndex + 1 : null)
        : (offer.indexToCollect ?? null),
      state: computeOfferState(offer),
      sub_type: offer?.subType ?? null,
      start_date: offer.schedule?.timeFrames[0]?.startTime,
      end_date: offer.schedule?.timeFrames[0]?.endTime,
      expiration_date: isRewardCalendar ? (offer.rewardCalendar?.nextUnlockAt ?? null) : null,
      products: offer.productsSequence[activeIndex].products.map((product: ProductData) => ({
        product_id: product.publisherProductId,
        product_name: product.name,
        product_type: product.productType,
        product_quantity: product.quantity,
      })),
      priceInUsdCents: offer.productsSequence[activeIndex]?.prices?.priceInUsdCents ?? 0,
      trigger_name: computeTriggerName(offer),
      trigger_frequency: offer.displayRule?.frequency ?? null,
      time_left: computeTimeLeftSec(offer),
      badge_ind: computeBadgeInd(offer),
    };
  });
};

const getActiveTimeFrameEndsAtMs = (
  schedule: OfferData['schedule'] | undefined,
): number | null => {
  if (!schedule || schedule.permanent) return null;

  const timeFrames = schedule.timeFrames as TimeFrameObject[] | undefined;
  if (!timeFrames?.length) return null;

  const now = Date.now();
  for (const frame of timeFrames) {
    const start = new Date(frame.startTime).getTime();
    const end = new Date(frame.endTime).getTime();
    if (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      start <= now &&
      now <= end
    ) {
      return end;
    }
  }
  return null;
};
